/**
 * Cookiebot Consent Utility
 *
 * Provides type-safe access to Cookiebot consent state and consent-aware
 * localStorage operations for GDPR compliance.
 *
 * Consent Categories (per Cookiebot):
 * - necessary: Essential cookies (always allowed)
 * - preferences: Functionality/preference cookies
 * - statistics: Analytics cookies
 * - marketing: Marketing/advertising cookies
 */

// Cookiebot consent categories
export type ConsentCategory = 'necessary' | 'preferences' | 'statistics' | 'marketing';

// Cookiebot global object interface
interface CookiebotConsent {
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
}

interface CookiebotInstance {
  consent: CookiebotConsent;
  consented: boolean;
  declined: boolean;
  hasResponse: boolean;
  show: () => void;
  hide: () => void;
  renew: () => void;
  withdraw: () => void;
}

// Extend Window interface for Cookiebot
declare global {
  interface Window {
    Cookiebot?: CookiebotInstance;
    CookiebotCallback_OnAccept?: () => void;
    CookiebotCallback_OnDecline?: () => void;
  }
}

/**
 * Check if we're running in a browser environment
 */
const isBrowser = (): boolean => typeof window !== 'undefined';

/**
 * Get the Cookiebot instance if available
 */
const getCookiebot = (): CookiebotInstance | undefined => {
  if (!isBrowser()) return undefined;
  return window.Cookiebot;
};

/**
 * Check if user has given consent for a specific category
 */
export const hasConsent = (category: ConsentCategory): boolean => {
  const cookiebot = getCookiebot();

  // Necessary cookies are always allowed
  if (category === 'necessary') return true;

  // If Cookiebot isn't loaded yet, assume no consent
  if (!cookiebot) return false;

  // If user hasn't responded yet, assume no consent
  if (!cookiebot.hasResponse) return false;

  return cookiebot.consent[category] === true;
};

/**
 * Check if user has responded to the consent dialog
 */
export const hasConsentResponse = (): boolean => {
  const cookiebot = getCookiebot();
  if (!cookiebot) return false;
  return cookiebot.hasResponse;
};

/**
 * Show the Cookiebot consent dialog
 */
export const showConsentDialog = (): void => {
  const cookiebot = getCookiebot();
  if (cookiebot) {
    cookiebot.renew();
  }
};

/**
 * Consent-aware localStorage wrapper
 * Only writes to localStorage if the user has given consent for the specified category
 */
export const consentStorage = {
  /**
   * Get item from localStorage (always allowed to read)
   */
  getItem: (key: string): string | null => {
    if (!isBrowser()) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  /**
   * Set item in localStorage only if consent is given for the category
   * @returns true if the item was set, false if consent was not given
   */
  setItem: (key: string, value: string, category: ConsentCategory): boolean => {
    if (!isBrowser()) return false;

    if (!hasConsent(category)) {
      return false;
    }

    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Remove item from localStorage (always allowed)
   */
  removeItem: (key: string): void => {
    if (!isBrowser()) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  },
};

// Type for consent change callback
type ConsentCallback = () => void;

// Store callbacks for consent changes
const consentCallbacks: ConsentCallback[] = [];

/**
 * Register a callback to be called when consent changes
 */
export const onConsentChange = (callback: ConsentCallback): (() => void) => {
  consentCallbacks.push(callback);

  // Return unsubscribe function
  return () => {
    const index = consentCallbacks.indexOf(callback);
    if (index > -1) {
      consentCallbacks.splice(index, 1);
    }
  };
};

/**
 * Initialize consent event listeners
 * Should be called once in gatsby-browser.ts
 *
 * Note: Since Cookiebot loads post-hydrate, this handles the race condition
 * where returning users may already have consent state when Cookiebot loads.
 */
export const initConsentListeners = (): void => {
  if (!isBrowser()) return;

  const notifyCallbacks = () => {
    consentCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in consent callback:', error);
      }
    });
  };

  // Listen for Cookiebot events
  window.addEventListener('CookiebotOnAccept', notifyCallbacks);
  window.addEventListener('CookiebotOnDecline', notifyCallbacks);

  // Handle returning users: Cookiebot loads post-hydrate, so we need to check
  // if consent state already exists from a previous session. Listen for the
  // CookiebotOnLoad event which fires when Cookiebot initializes with existing consent.
  window.addEventListener('CookiebotOnLoad', () => {
    if (window.Cookiebot?.hasResponse) {
      // Defer to next tick to allow components to mount first
      setTimeout(notifyCallbacks, 0);
    }
  });
};
